import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import InspectionSteps from '../../../InspectionHistory/components/InspectionSteps/InspectionSteps';
import InspectionComments from '../InspectionComments/InspectionComments';
import InspectionService from 'services/ApiService/InspectionService/inspectionService';
import { IComment } from '../../../Inspection/Inspection.types';
import * as S from './TabHistory.styles';
import { ITabHistoryProps } from './TabHistory.types';

const TabHistory = ({ inspection }: ITabHistoryProps) => {
  const [comments, setComments] = useState<IComment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  /**
   * Carrega comentários da API ao montar o componente ou quando inspection muda
   */
  useEffect(() => {
    const loadComments = async () => {
      if (!inspection?.id) return;

      // Se inspection já tem comments, usa eles (vem do GET /inspections/{id})
      if (inspection.comments && inspection.comments.length > 0) {
        setComments(inspection.comments);
        return;
      }

      // Caso contrário, faz chamada separada
      setIsLoadingComments(true);

      try {
        const response = await InspectionService.getComments(inspection.id);
        setComments(response.comments);
      } catch (error: unknown) {
        console.error('Erro ao carregar comentários:', error);

        // Tratamento específico de erros
        const axiosError = error as { response?: { status?: number } };

        if (axiosError.response?.status === 401) {
          toast.error('Sessão expirada. Por favor, faça login novamente.');
        } else if (axiosError.response?.status === 404) {
          // Inspeção não encontrada - possível inconsistência
          console.warn('Inspeção não encontrada ao carregar comentários');
        } else if (!axiosError.response) {
          toast.error('Erro de conexão. Verifique sua internet e tente novamente.');
        } else {
          toast.error('Erro ao carregar comentários.');
        }
      } finally {
        setIsLoadingComments(false);
      }
    };

    loadComments();
  }, [inspection?.id, inspection?.comments]);

  /**
   * Adiciona novo comentário via API
   */
  const handleCommentAdd = useCallback(
    async (commentText: string): Promise<void> => {
      if (!inspection?.id) {
        toast.error('Erro: inspeção não identificada.');
        throw new Error('Inspection ID is required');
      }

      // Validações
      const trimmedText = commentText.trim();

      if (!trimmedText) {
        toast.warning('Por favor, digite um comentário');
        throw new Error('Comment text is required');
      }

      if (trimmedText.length > 2000) {
        toast.error('O comentário não pode ter mais de 2000 caracteres');
        throw new Error('Comment text exceeds 2000 characters');
      }

      try {
        // Chamada real à API
        const response = await InspectionService.createComment(inspection.id, trimmedText);

        // Adiciona o novo comentário à lista local
        setComments((prev) => [...prev, response.comment]);

        // Feedback de sucesso
        toast.success('Comentário adicionado com sucesso!');
      } catch (error: unknown) {
        console.error('Erro ao adicionar comentário:', error);

        // Tratamento específico de erros da API
        const axiosError = error as { response?: { status?: number } };

        if (axiosError.response?.status === 400) {
          toast.error('Comentário inválido. Verifique o texto e tente novamente.');
        } else if (axiosError.response?.status === 401) {
          toast.error('Sessão expirada. Por favor, faça login novamente.');
        } else if (axiosError.response?.status === 404) {
          toast.error('Inspeção não encontrada.');
        } else if (!axiosError.response) {
          toast.error('Erro de conexão. Verifique sua internet e tente novamente.');
        } else {
          toast.error('Erro ao adicionar comentário. Tente novamente.');
        }

        // Repropaga o erro para que o componente de comentários possa lidar
        throw error;
      }
    },
    [inspection?.id]
  );

  return (
    <S.Container data-testid="tab-history">
      <S.HistorySection data-testid="history-section">
        <InspectionSteps inspection={inspection} />
      </S.HistorySection>

      <S.Divider />

      <S.CommentsSection data-testid="comments-section">
        <InspectionComments
          inspection={inspection}
          comments={comments}
          onCommentAdd={handleCommentAdd}
          isLoading={isLoadingComments}
        />
      </S.CommentsSection>
    </S.Container>
  );
};

export default TabHistory;
